import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  Table,
  Space,
  Typography,
  Alert,
  Tooltip,
  Select,
  Button,
  Skeleton,
} from 'antd';
import {
  PlayCircleOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
  RobotOutlined,
  BranchesOutlined,
  ClearOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { HtmlReportDrawer } from './HtmlReportDrawer';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import type { RunSummary } from '@/api/validationRuns';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { useRunLinks } from '@/features/scenario-intelligence/workspace/paths';

const { Text } = Typography;

// Live feed of aivalidation.ValidationRun rows for the active merchant.
// Filterable by agent, environment, status, and time window. Click any row
// to drill into the full chat-bubble transcript (RunDetailPage — coming next).

const STATUS_TONE: Record<string, PillTone> = {
  Completed: 'green',
  Running: 'ink',
  Aborted: 'amber',
  Errored: 'red',
};

const ENV_TONE: Record<string, PillTone> = {
  PROD: 'red',
  Prod: 'red',
  QA: 'amber',
  qa: 'amber',
  local: 'slate',
  Local: 'slate',
};

export const RecentRunsPage = observer(function RecentRunsPage() {
  const {
    validationRunsStore: store,
    merchantContextStore: ctx,
    themeStore,
  } = useStore();
  const isDark = themeStore.mode === 'dark';
  const navigate = useNavigate();
  const links = useRunLinks();

  // Local state for the in-place HTML report drawer. Lifted up here (not in
  // a row component) because antd's Drawer should mount once at the page
  // level, not per-row — keeps the iframe alive across selections.
  const [reportRunId, setReportRunId] = useState<number | null>(null);
  const [reportPassed, setReportPassed] = useState<boolean | null>(null);
  const [reportSubtitle, setReportSubtitle] = useState<string>('');

  useEffect(() => {
    if (ctx.selectedMerchantId != null && store.runs.length === 0 && !store.isLoading) {
      void store.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedMerchantId]);

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<PlayCircleOutlined />}
          title="Pick a merchant to begin"
          description="Validation runs are scoped to a single merchant via ai.AssistantAI."
          size="lg"
        />
      </SectionCard>
    );
  }

  const filterCount =
    (store.agentFilter ? 1 : 0) +
    (store.environmentFilter ? 1 : 0) +
    (store.statusFilter ? 1 : 0);

  const totalScenarios = store.runs.reduce((s, r) => s + r.totalScenarios, 0);
  const passedScenarios = store.runs.reduce((s, r) => s + r.passedScenarios, 0);
  const aggregatePassRate =
    totalScenarios > 0 ? Math.round((100 * passedScenarios) / totalScenarios) : null;

  const columns: ColumnsType<RunSummary> = [
    {
      title: 'Run',
      key: 'run',
      width: 90,
      render: (_t, row) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12.5,
            fontWeight: 600,
            color: isDark ? colors.slate[100] : colors.slate[900],
          }}
        >
          #{row.id}
        </Text>
      ),
    },
    {
      title: 'Agent',
      key: 'agent',
      render: (_t, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: isDark ? `${colors.ink[700]}55` : colors.ink[50],
              color: colors.ink[600],
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              flexShrink: 0,
            }}
          >
            <RobotOutlined />
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: isDark ? colors.slate[100] : colors.slate[900],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 240,
              }}
            >
              {row.agentName ?? `Agent #${row.aiAgentId}`}
            </div>
            {row.suiteSlug && (
              <div
                style={{
                  fontSize: 10.5,
                  fontFamily: "'JetBrains Mono', monospace",
                  color: isDark ? colors.slate[400] : colors.slate[500],
                  marginTop: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 240,
                }}
              >
                {row.suiteSlug}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Outcome',
      key: 'outcome',
      width: 200,
      sorter: (a, b) => Number(a.overallPassRate) - Number(b.overallPassRate),
      render: (_t, row) => {
        const pct = Number(row.overallPassRate);
        const tone =
          row.deploymentBlocked
            ? colors.red[600]
            : pct >= 95
            ? colors.green[600]
            : pct >= 80
            ? colors.amber[600]
            : colors.red[600];
        const Icon =
          row.failedScenarios === 0 ? CheckCircleFilled : row.deploymentBlocked ? WarningFilled : CloseCircleFilled;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon style={{ color: tone, fontSize: 16, flexShrink: 0 }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: tone,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {pct.toFixed(0)}%
              </div>
              <div style={{ fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500] }}>
                {row.passedScenarios}/{row.totalScenarios} passed
              </div>
            </div>
            {/* Mini bar */}
            <span
              style={{
                width: 50,
                height: 5,
                borderRadius: 2,
                background: isDark ? colors.slate[700] : colors.slate[100],
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
              }}
              aria-hidden
            >
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${Math.min(100, pct)}%`,
                  background: tone,
                }}
              />
            </span>
          </div>
        );
      },
    },
    {
      title: 'Run type',
      dataIndex: 'runType',
      key: 'runType',
      width: 110,
      render: (v?: string) =>
        v ? <StatusPill tone={v === 'MultiTurn' ? 'violet' : 'slate'}>{v}</StatusPill> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Env',
      dataIndex: 'environment',
      key: 'environment',
      width: 90,
      render: (v?: string) =>
        v ? <StatusPill tone={ENV_TONE[v] ?? 'slate'}>{v}</StatusPill> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Trigger',
      dataIndex: 'triggerSource',
      key: 'triggerSource',
      width: 110,
      render: (v?: string) => (v ? <StatusPill tone="ink">{v}</StatusPill> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v?: string) =>
        v ? <StatusPill tone={STATUS_TONE[v] ?? 'slate'}>{v}</StatusPill> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Elapsed',
      dataIndex: 'totalElapsedMs',
      key: 'elapsed',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.totalElapsedMs - b.totalElapsedMs,
      render: (n: number) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
        >
          {formatElapsed(n)}
        </Text>
      ),
    },
    {
      title: 'Run at',
      dataIndex: 'runDate',
      key: 'runDate',
      width: 170,
      sorter: (a, b) => a.runDate.localeCompare(b.runDate),
      defaultSortOrder: 'descend' as const,
      render: (v: string) => {
        const d = new Date(v);
        return (
          <Text style={{ fontSize: 12, color: isDark ? colors.slate[300] : colors.slate[600] }}>
            {d.toLocaleDateString()}{' '}
            <span style={{ opacity: 0.7 }}>
              {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </Text>
        );
      },
    },
    {
      title: 'SHA',
      key: 'sha',
      width: 130,
      render: (_t, row) =>
        row.gitCommitSha ? (
          <Tooltip title={`${row.gitBranch ?? 'branch'} · ${row.gitCommitSha}`}>
            <Text
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: isDark ? colors.slate[400] : colors.slate[500],
              }}
            >
              <BranchesOutlined style={{ fontSize: 10, marginRight: 4 }} />
              {row.gitCommitSha.slice(0, 7)}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '',
      key: 'report',
      width: 100,
      align: 'right' as const,
      fixed: 'right' as const,
      // Stop click propagation so the row's onRow click handler (which
      // navigates to RunDetail) doesn't fire on the report button.
      onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
      render: (_t, row) => {
        const passRate =
          row.totalScenarios > 0
            ? row.passedScenarios / row.totalScenarios
            : null;
        const passed = passRate != null ? passRate >= 0.999 : null;
        const subtitle =
          `${(row.agentName ?? `Agent #${row.aiAgentId}`)} · ` +
          new Date(row.runDate).toLocaleString() +
          (row.environment ? ` · ${row.environment}` : '') +
          (row.triggerSource ? ` · ${row.triggerSource}` : '');
        return (
          <Tooltip title="View the formatted chat-bubble report for this run">
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => {
                setReportRunId(row.id);
                setReportPassed(passed);
                setReportSubtitle(subtitle);
              }}
            >
              Report
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="green"
        icon={<PlayCircleOutlined />}
        title={
          <Space size={6} align="center">
            <span>Recent runs</span>
            <HelpHotspot topic="validations.runs.recent" />
          </Space>
        }
        subtitle={`Live feed of every validation run in the last ${store.days} days.`}
        count={
          aggregatePassRate != null
            ? `${store.runs.length} runs · ${aggregatePassRate}% scenarios pass`
            : store.runs.length
        }
        countLabel={store.runs.length === 1 ? 'run' : 'runs'}
        actions={
          <Space>
            <Select
              style={{ width: 160 }}
              value={store.days}
              onChange={(v) => store.setDays(v)}
              options={[
                { value: 1, label: 'Last 24 h' },
                { value: 7, label: 'Last 7 days' },
                { value: 30, label: 'Last 30 days' },
                { value: 90, label: 'Last 90 days' },
              ]}
            />
            <AgentPickerTrigger
              value={store.agentFilter}
              onChange={(id) => store.setAgentFilter(id)}
              placeholder="Any agent"
              clearLabel="Any agent — show all"
            />
            <Select
              allowClear
              style={{ width: 130 }}
              placeholder="Env"
              value={store.environmentFilter ?? undefined}
              onChange={(v) => store.setEnvironmentFilter(v ?? null)}
              options={[
                { value: 'PROD', label: 'PROD' },
                { value: 'QA', label: 'QA' },
                { value: 'local', label: 'local' },
              ]}
            />
            <Select
              allowClear
              style={{ width: 140 }}
              placeholder="Status"
              value={store.statusFilter ?? undefined}
              onChange={(v) => store.setStatusFilter(v ?? null)}
              options={[
                { value: 'Completed', label: 'Completed' },
                { value: 'Running', label: 'Running' },
                { value: 'Aborted', label: 'Aborted' },
                { value: 'Errored', label: 'Errored' },
              ]}
            />
            {filterCount > 0 && (
              <Button
                type="text"
                size="small"
                icon={<ClearOutlined />}
                onClick={() => {
                  store.setAgentFilter(null);
                  store.setEnvironmentFilter(null);
                  store.setStatusFilter(null);
                }}
              >
                Clear
              </Button>
            )}
            <Button icon={<ReloadOutlined />} loading={store.isLoading} onClick={() => void store.fetch()}>
              Refresh
            </Button>
          </Space>
        }
      />

      {store.error && (
        <Alert type="error" showIcon message="Failed to load runs" description={store.error} closable />
      )}

      {store.isLoading && store.runs.length === 0 ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      ) : store.runs.length === 0 ? (
        <SectionCard>
          <BrandedEmpty
            icon={<PlayCircleOutlined />}
            title={filterCount > 0 ? 'No runs match' : 'No runs in window'}
            description={
              filterCount > 0
                ? 'Try clearing filters or widening the time range.'
                : 'Trigger a run from the AiAgentValidations test project — rows will land here.'
            }
          />
        </SectionCard>
      ) : (
        <SectionCard noPadding>
          <Table<RunSummary>
            rowKey="id"
            columns={columns}
            dataSource={store.runs}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (t) => `${t} runs`,
              style: { padding: '12px 18px', margin: 0 },
            }}
            onRow={(row) => ({
              onClick: () => {
                trackEvent('Run.Opened', { runId: row.id });
                navigate(links.run(row.id));
              },
              style: { cursor: 'pointer' },
            })}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </SectionCard>
      )}

      <HtmlReportDrawer
        runId={reportRunId}
        passed={reportPassed}
        subtitle={reportSubtitle}
        onClose={() => setReportRunId(null)}
      />
    </div>
  );
});

function formatElapsed(ms: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms} ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const min = sec / 60;
  return `${min.toFixed(1)}m`;
}
